import { Product } from 'utils/helpers/product';

export function getSmartTagNamesFromProducts(products: Product[]): string[] {
  const seen = new Map<string, string>();

  products.forEach((product) => {
    product.POSMetaData?.activeBatchTags?.forEach((tag) => {
      if (!tag?.tagId || !tag.tagName) {
        return;
      }
      const key = tag.tagName.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, tag.tagName);
      }
    });
  });

  return Array.from(seen.values());
}

export function compareSmartTagNamesNaturally(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

export function smartTagNameMatchesAny(tagName: string, matchStrings: string[]): boolean {
  const lowerTagName = tagName.toLowerCase();
  return matchStrings.some((match) => lowerTagName.includes(match.toLowerCase()));
}

export function filterSmartTagNamesByMatch(tagNames: string[], matchStrings: string[]): string[] {
  return tagNames.filter((tagName) => smartTagNameMatchesAny(tagName, matchStrings));
}

/**
 * Does this product carry any of the exact, currently-selected smart tag names? Single source of
 * truth for "does the active smart tags filter apply to this product" — used both for the organic
 * product list (`use-filter-products-query-by-smart-tags`) and for sponsored/ad candidates
 * (`useSponsoredProductsFilter`), so a sponsored product can never bypass the smart tags filter.
 * An empty `selectedSmartTags` means the filter isn't active, so every product passes.
 */
export function productMatchesSelectedSmartTags(product: Product, selectedSmartTags: string[]): boolean {
  if (!selectedSmartTags.length) {
    return true;
  }

  const normalizedSelected = new Set(selectedSmartTags.map((tag) => tag.toLowerCase()));
  const productSmartTagNames = product.POSMetaData?.activeBatchTags
    ?.map((tag) => tag?.tagName?.toLowerCase())
    .filter((tagName): tagName is string => Boolean(tagName));

  return Boolean(productSmartTagNames?.some((tagName) => normalizedSelected.has(tagName)));
}
